import clsx from 'clsx';
import { VisuallyHidden } from 'radix-ui'
import React, { useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';

import type { Notebook } from '@customTypes/notebook';

import { getUserFallback } from '@helpers/user';

import { unpublishNotebook } from '@services/NotebooksService';
import { fetchPublishedSummaries } from '@services/SourcesService';

import { useSession } from '@root/SessionContext';

import { useDataSourceItemActions } from '@hooks/useDataSourceItemActions';
import { useFilterAndSort } from '@hooks/useFilterAndSort';
import { useInfiniteScrollQuery } from '@hooks/useInfiniteScrollQuery';
import { useRememberedSearchParams } from '@hooks/useRememberedSearchParams';
import { useToast } from '@hooks/useToast';

import Avatar from '@ui-components/layout/Avatar';
import EmptyState from "@ui-components/layout/EmptyState";
import Icon from '@ui-components/layout/Icon';
import { TableSkeleton } from '@ui-components/layout/LoadingIndicator';
import MetaList from '@ui-components/layout/MetaList';

import AddToNotebookModal from '@components/AddToNotebookModal';
import ConfirmPauseModal from '@components/ConfirmPauseModal';
import ConfirmPublishModal from '@components/ConfirmPublishModal';
import DataSourceItemActionsMenu from '@components/DataSourceItemActionsMenu';
import DataSourceItemPreviewModal from '@components/DataSourceItemPreviewModal';
import DataSourceItemTitle from '@components/DataSourceItemTitle';
import EditableTitle from '@components/EditableTitle';
import PriorityTierToggle from '@components/PriorityTierToggle';
import SortableHeader from '@components/SortableHeader';
import { SortModal } from '@components/SortModal';

import ActiveFilters from './ActiveFilters';
import { DataSourcesDocumentsFilter } from './DataSourcesDocumentsFilter';
import styles from './DataSourcesDocumentsPanel.module.css';

const messages = defineMessages({
  unpublished: {
    id: 'notebookSummaryPanel.unpublished',
    defaultMessage: 'Unpublished',
    description: 'Confirmation title that appears when the notebook was unpublished',
  },
  unpublishSuccess: {
    id: 'global.notebookUnpublishSuccess',
    defaultMessage: 'Notebook summary unpublished successfully.',
    description: 'Message for successful notebook unpublish action',
  },
  unpublishError: {
    id: 'global.notebookUnpublishError',
    defaultMessage: 'Failed to unpublish notebook summary.',
    description: 'Message for failed notebook unpublish action',
  },
});

type NotebookRowProps = {
  nb: Notebook;
  onPreview: (n: Notebook) => void;
  onAddToNotebook: (n: Notebook) => void;
  onPause: (n: Notebook) => void;
  onUnpublish: (n: Notebook) => void;
  onRetry: (n: Notebook) => void;
  onTogglePriorityTier: (n: Notebook) => void;
  onCopyPermalink: (n: Notebook) => void;
};

const NotebookRow: React.FC<NotebookRowProps> = React.memo(({ nb, onPreview, onAddToNotebook, onPause, onUnpublish, onRetry, onTogglePriorityTier, onCopyPermalink }) => (
  <tr className={clsx({ [styles.rowPaused]: nb.active === false || nb.sync_status != 'complete' })}>
    <td className="table-priority-cell">
      <PriorityTierToggle
        disabled={nb.sync_status === 'pending'}
        priorityTier={nb.priority_tier}
        onToggle={() => onTogglePriorityTier(nb)}
        size="small"
        variant="filled"
      />
    </td>
    <td className={styles.rowPreview} onClick={() => onPreview(nb)}>
      <DataSourceItemTitle
        title={nb.name}
        syncStatus={nb.sync_status}
        active={nb.active}
      />
    </td>
    <td
      className={clsx(styles.rowPreview, styles.rowUsername)}
      title={nb.summary_updated_by && (nb.summary_updated_by?.name || nb.summary_updated_by?.email)}
      onClick={() => onPreview(nb)}
    >
      {nb.summary_updated_by ? (
        <span>
          <Avatar
            editor
            picture={nb.summary_updated_by?.profile_picture_url}
            name={nb.summary_updated_by?.name}
            fallback={getUserFallback(nb.summary_updated_by?.name, nb.summary_updated_by?.email)}
          />
          <span>{nb.summary_updated_by?.name || nb.summary_updated_by?.email}</span>
        </span>
      ) : <span>—</span>}
    </td>
    <td
      className={clsx('table-date-cell', styles.rowPreview)}
      title={new Date(nb.updated_at).toLocaleString()}
      onClick={() => onPreview(nb)}
    >
        {new Date(nb.updated_at).toLocaleDateString()}
    </td>
    <td className="table-row-actions">
      <DataSourceItemActionsMenu
        item={nb}
        onPause={onPause}
        onUnpublish={onUnpublish}
        onRetry={nb.sync_status === 'error' ? onRetry : undefined}
        onAddToNotebook={onAddToNotebook}
        onPreview={onPreview}
        onCopyPermalink={onCopyPermalink}
        onTogglePriorityTier={onTogglePriorityTier}
        priorityTier={nb.priority_tier}
      />
    </td>
  </tr>
));
NotebookRow.displayName = 'NotebookRow';

const DataSourcesSummariesPanel = () => {
  const intl = useIntl();
  const hubId = useSession().currentHubId();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useRememberedSearchParams({
    routeId: '/data-sources/summaries',
    hubId,
    enabled: Boolean(hubId),
  });

  const [filterOpen, setFilterOpen] = useState(false);
  const { currentQuery: filter } = useFilterAndSort({});

  const {
    itemToAddToNotebook,
    previewId,
    closePreview,
    handleTogglePreviewPause,
    handleTogglePreviewPriorityTier,
    handleCopyPermalink,
    openPreview,
    setItemToAddToNotebook,
    itemToPause,
    setItemToPause,
    handlePauseConfirm,
    handleRetry,
    handleTogglePriorityTier,
  } = useDataSourceItemActions<Notebook>({
    listQueryKeys: [[`/hubs/${hubId}/notebooks?published=true`]],
    invalidationKeys: [],
    entityType: 'notebooks',
    permalink: {
      listPath: '/data-sources/summaries',
      itemPath: id => `/data-sources/summaries/${id}`,
    },
  });

  const [notebookToUnpublish, setNotebookToUnpublish] = useState<Notebook | null>(null);

  const {
    items: summaries,
    observerTarget,
    isFetchingNextPage,
    pagination,
    isPending,
  } = useInfiniteScrollQuery<Notebook>({
    queryKey: [`/hubs/${hubId}/notebooks?published=true`, `filter:${JSON.stringify(filter)}`],
    queryFn: (page, perPage) => fetchPublishedSummaries(hubId, page, perPage, filter),
    perPage: 20,
    enabled: Boolean(hubId),
    keepPreviousData: true,
  });

  if (isPending) return <TableSkeleton className="layout-main-column-scrollable-area" rows={20} />;

  const summariesTotalCount = pagination?.unfiltered_count ?? 0;

  return (
    <>
    <div className={clsx('layout-main-column-scrollable-area', styles.tableWrapper)}>
      <ConfirmPauseModal
        open={Boolean(itemToPause)}
        paused={itemToPause?.active === false}
        entityTitle={itemToPause?.name}
        entityType="Notebook"
        onCancel={() => setItemToPause(null)}
        onConfirm={handlePauseConfirm}
      />
      <AddToNotebookModal
        assetId={itemToAddToNotebook?.id}
        assetTitle={itemToAddToNotebook?.name}
        assetType="Notebook"
        open={Boolean(itemToAddToNotebook)}
        onOpenChange={() => setItemToAddToNotebook(null)}
      />
      <DataSourceItemPreviewModal
        open={Boolean(previewId)}
        id={previewId}
        key={previewId}
        type="Notebook"
        onOpenChange={closePreview}
        onTogglePause={previewId ? handleTogglePreviewPause : undefined}
        onTogglePriorityTier={previewId ? handleTogglePreviewPriorityTier : undefined}
      />
      <ConfirmPublishModal
        open={Boolean(notebookToUnpublish)}
        published
        title={notebookToUnpublish?.name}
        onCancel={() => setNotebookToUnpublish(null)}
        onConfirm={async () => {
          if (notebookToUnpublish) {
            try {
              await unpublishNotebook(notebookToUnpublish.id);
              queryClient.invalidateQueries({ queryKey: [`/hubs/${hubId}/notebooks?published=true`] });
              setNotebookToUnpublish(null);
              toast('success', intl.formatMessage(messages.unpublishSuccess), intl.formatMessage(messages.unpublished));
            } catch {
              toast('error', intl.formatMessage(messages.unpublishError));
            }
          }
        }}
      />
      <div className={styles.tableHeader}>
        <EditableTitle
          canEdit={false}
          className={styles.tableHeaderTitle}
          displayValue={
            <FormattedMessage
              id="global.dataSourcesSummariesPanelnotebooks"
              defaultMessage="Notebook summaries"
              description="Title for the Notebook summaries list"
            />
          }
          icon={<Icon key="icon" aria-hidden className="no-mobile-access" id="notebook_source" />}
          renderMeta={
            <MetaList>
              <FormattedMessage
                id="global.entriesCount"
                defaultMessage="<strong>Entries:</strong> {count}"
                description="Text for the count of entries in the table below"
                values={{
                  count: summariesTotalCount,
                  strong: msg => <strong key="strong">{msg}</strong>,
                }}
                tagName={MetaList.Item}
              />
            </MetaList>
          }
        />
        <div className={styles.tableHeaderButtons}>
          <DataSourcesDocumentsFilter type="summary" open={filterOpen} onOpenChange={setFilterOpen} />
          <SortModal type="summary" />
        </div>
      </div>
      <ActiveFilters type="summary" resultCount={pagination?.total_count} onEdit={() => setFilterOpen(true)} />
      <table aria-labelledby="tableCaption">
        <VisuallyHidden.Root asChild>
          <caption id="tableCaption">
            <FormattedMessage
              id="dataSourcesSummariesPanel.tableCaption"
              defaultMessage="Table with notebook summaries"
              description="Accessibility caption for the table that shows documents and the type of documents they are"
            />
          </caption>
        </VisuallyHidden.Root>
        <thead>
          <tr>
            <SortableHeader type="summary" sortKey="priority_tier" className="table-priority-cell">
              <Icon id="important" aria-hidden />
              <VisuallyHidden.Root>
                <FormattedMessage
                  id="global.priorityTier"
                  defaultMessage="Priority tier"
                  description="Label for the priority tier"
                />
              </VisuallyHidden.Root>
            </SortableHeader>
            <SortableHeader type="summary" sortKey="title">
              <FormattedMessage
                id="dataSourcesSummariesPanel.notebookName"
                defaultMessage="Notebook name"
                description="Column label for the name of notebook listed in table"
              />
            </SortableHeader>
            <SortableHeader type="summary" sortKey="last_summary_updated_by_id" className={styles.tableHeaderUsernameColumn}>
              <FormattedMessage
                id="dataSourcesSummariesPanel.lastEditor"
                defaultMessage="Last edited by"
                description="Column label for the last editor of summaries listed in table"
              />
            </SortableHeader>
            <SortableHeader type="summary" sortKey="updated_at" className="table-date-cell">
              <FormattedMessage
                id="global.dateModified"
                defaultMessage="Date modified"
                description="Reusable label for the date the item was last modified"
              />
            </SortableHeader>
            <th scope="col" className="table-row-actions">
              <FormattedMessage
                id="global.actions"
                defaultMessage="Actions"
                description="Reusable label for actions list"
              />
            </th>
          </tr>
        </thead>
        <tbody>
        {summaries.length > 0 ? (
          summaries.map((nb: Notebook) => (
            <NotebookRow
              key={nb.id}
              nb={nb}
              onPreview={openPreview}
              onAddToNotebook={setItemToAddToNotebook}
              onPause={setItemToPause}
              onUnpublish={setNotebookToUnpublish}
              onRetry={handleRetry}
              onTogglePriorityTier={handleTogglePriorityTier}
              onCopyPermalink={handleCopyPermalink}
            />
          ))
        ) : (
          <tr className={styles.noDocuments}>
            <td colSpan={5}>
              <EmptyState
                actions={<DataSourcesDocumentsFilter type="summary" />}
                icon="document_dashed"
                message={
                  <>
                    <FormattedMessage
                      id="global.dataSourcesSummariesPaneltableEmpty"
                      defaultMessage="There are no published notebook summaries."
                      description="Empty message for table with no rows"
                    />
                    <br />
                    <br />
                    <FormattedMessage
                      id="global.dataSourcesSummariesFiltersAttention"
                      defaultMessage="Try a different set of filters to show notebook summaries."
                      description="Empty message to let users know they can change filters"
                    />
                  </>
                }
              />
            </td>
          </tr>
        )}
        </tbody>
        {(summariesTotalCount > 0) && (
          <tfoot className={styles.scrollObserver}>
            <tr>
              <td colSpan={5}>
                <span ref={observerTarget} className="scroll-observer-large" />
                {isFetchingNextPage && (
                  <FormattedMessage
                    id="global.loadingMore"
                    defaultMessage="Loading more…"
                    description="Message shown when loading more items"
                    tagName="p"
                  />
                )}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
    </>
  );
};

export default DataSourcesSummariesPanel;
